import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PACKS = [
  {
    id: 'starter',
    name: 'Starter',
    totalCredits: 4,
    price: '3.49',
    cta: 'Get Started',
    bonusBadge: null,
    bonusDesc: 'Starter pack to get you going.',
    highlight: false,
    label: null,
  },
  {
    id: 'value_10',
    name: 'Value Pack',
    totalCredits: 12,
    price: '7.99',
    cta: 'Get 12 Credits',
    bonusBadge: '+20% More',
    bonusDesc: 'Save more compared to Starter.',
    highlight: false,
    label: null,
  },
  {
    id: 'popular',
    name: 'Most Popular',
    totalCredits: 24,
    price: '9.99',
    cta: 'Get 24 Credits',
    bonusBadge: '+33% More',
    bonusDesc: 'Most popular for quick payment recovery.',
    highlight: true,
    label: 'Most Popular',
  },
  {
    id: 'best_value',
    name: 'Best Value',
    totalCredits: 48,
    price: '14.99',
    cta: 'Get 48 Credits',
    bonusBadge: '+50% More',
    bonusDesc: 'Get 50% more credits — best value.',
    highlight: false,
    label: 'Best Value',
  },
  {
    id: 'power',
    name: 'Power Pack',
    totalCredits: 90,
    price: '24.99',
    cta: 'Get 90 Credits',
    bonusBadge: '+50% More',
    bonusDesc: 'Maximum credits for power users.',
    highlight: false,
    label: null,
  },
];

export default function PricingModal({ open, onClose, trigger }) {
  const { fetchPlanInfo } = useAuth();
  const [loading, setLoading] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [customLoading, setCustomLoading] = useState(false);

  const customCredits = (() => {
    const val = parseFloat(customAmount);
    if (!val || val < 10) return 0;
    return Math.round(20.57 * Math.pow(val - 10, 0.572));
  })();

  const handleCheckout = async (packId) => {
    setLoading(packId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { package_id: packId, origin_url: originUrl },
        { withCredentials: true }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch {
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleCustomCheckout = async () => {
    const amount = parseFloat(customAmount);
    if (!amount || amount < 10) {
      toast.error('Minimum custom amount is £10');
      return;
    }
    setCustomLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/api/payments/custom-checkout`,
        { amount, origin_url: originUrl },
        { withCredentials: true }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch {
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCustomLoading(false);
    }
  };

  const triggerMessages = {
    no_credits: "You've run out of credits",
    letter_limit: "You need credits to generate emails",
    download: "You need credits to download",
    response_limit: "You need credits to generate replies",
    tone: "Savage tone costs extra credits",
    template: "Premium templates require credits",
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-[#0a0a0f] border border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="pricing-modal">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-2xl font-bold text-center" data-testid="pricing-modal-title">
            {triggerMessages[trigger] || 'Get More Credits'}
          </DialogTitle>
          <p className="text-center text-white/50 text-sm mt-2">
            Credits power every feature. Buy once, use anytime.
          </p>
        </DialogHeader>

        {/* Pack Cards */}
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          {PACKS.map((pack) => (
            <div
              key={pack.id}
              data-testid={`pack-card-${pack.id}`}
              className={`relative rounded-xl border p-4 flex flex-col transition-all duration-200 hover:-translate-y-0.5 ${
                pack.highlight
                  ? 'border-[#FF4500] bg-[#FF4500]/[0.05] shadow-[0_0_20px_-6px_rgba(255,69,0,0.2)]'
                  : pack.id === 'best_value'
                    ? 'border-white/40 bg-white/[0.02] hover:border-white/60'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20'
              }`}
            >
              {pack.label && (
                <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap ${
                  pack.highlight ? 'bg-[#FF4500] text-white' : 'bg-white text-black'
                }`}>
                  {pack.label}
                </span>
              )}
              <h3 className="font-semibold text-sm text-white mt-1 mb-1.5">{pack.name}</h3>
              <div className="mb-1">
                <span className="text-2xl font-bold">£{pack.price}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-white/50 text-xs">{pack.totalCredits} credits</span>
                {pack.bonusBadge && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FF4500] text-white">
                    {pack.bonusBadge}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-white/40 mb-3 flex-1">{pack.bonusDesc}</p>
              <Button
                data-testid={`checkout-btn-${pack.id}`}
                onClick={() => handleCheckout(pack.id)}
                disabled={loading !== null}
                className={`w-full text-xs py-2 ${
                  pack.highlight
                    ? 'bg-[#FF4500] hover:bg-[#CC3700] text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                {loading === pack.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  pack.cta
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="px-6 pb-4">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
            <h4 className="text-sm font-semibold text-white/70 mb-3" data-testid="custom-amount-title">Custom amount</h4>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm font-medium">£</span>
                <input
                  type="number"
                  min="10"
                  max="500"
                  step="1"
                  placeholder="10"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full h-10 pl-7 pr-3 rounded-lg bg-black/50 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#FF4500]/40 focus:border-[#FF4500]/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid="custom-amount-input"
                />
              </div>
              <Button
                onClick={handleCustomCheckout}
                disabled={customLoading || customCredits < 1 || loading !== null}
                className="bg-[#FF4500] hover:bg-[#CC3700] text-white text-sm px-5 h-10 whitespace-nowrap"
                data-testid="custom-amount-btn"
              >
                {customLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : customCredits > 0 ? (
                  `Buy ${customCredits} Credit${customCredits !== 1 ? 's' : ''}`
                ) : (
                  'Buy Credits'
                )}
              </Button>
            </div>
            <p className="text-[11px] text-white/30 mt-2">£10 minimum. Credits scale with amount — bigger spends unlock more value.</p>
          </div>
        </div>

        {/* How Credits Work */}
        <div className="px-6 pb-6">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4">
            <h4 className="text-sm font-semibold text-white/80 mb-3" data-testid="credits-explainer-title">How Credits Work</h4>
            <div className="grid grid-cols-4 gap-3 text-center mb-3">
              <div>
                <p className="text-lg font-bold text-white">1</p>
                <p className="text-[11px] text-white/40">Reply to a client</p>
              </div>
              <div>
                <p className="text-lg font-bold text-white">1</p>
                <p className="text-[11px] text-white/40">Create an invoice</p>
              </div>
              <div>
                <p className="text-lg font-bold text-white">1</p>
                <p className="text-[11px] text-white/40">Download</p>
              </div>
              <div>
                <p className="text-lg font-bold text-white">3</p>
                <p className="text-[11px] text-white/40">Full email set</p>
              </div>
            </div>
            <p className="text-[11px] text-white/30 text-center">Most users recover payments in under 3 messages.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
